import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { finalize } from 'rxjs';
import { ApPayment, ApPaymentSearchCommand, ApPaymentStatus } from '../../../models/ap-payment/ap-payment';
import { ApPaymentProxyService } from '../../../proxies/ap-payment.proxy.service';
import { BankAccountProxyService } from '../../../proxies/bank-account.proxy.service';
import { BankAccount } from '../../../models/bank-account/bank-account';
import { currencyLabelFromApi } from '../../../utilities/currency.util';
import { EnumService } from '../../../services/enum.service';
import { ProviderFilterService } from '../../../services/provider-filter.service';

interface SelectOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'app-ap-payments',
  templateUrl: './ap-payments.component.html',
  styleUrl: './ap-payments.component.scss',
})
export class ApPaymentsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly service = inject(ApPaymentProxyService);
  private readonly bankAccountService = inject(BankAccountProxyService);
  private readonly enumService = inject(EnumService);
  private readonly providerFilter = inject(ProviderFilterService);
  private readonly translateService = inject(TranslateService);

  // Two ways to start an AP payment: settle a provider's posted documents directly, or fulfil an approved payment request.
  readonly speedDialItems: MenuItem[] = [
    {
      label: this.translate('ApPayment_New_FromDocument', 'AP Document'),
      icon: 'pi pi-file',
      command: () => this.redirectToNew('document'),
    },
    {
      label: this.translate('ApPayment_New_FromRequest', 'Payment Request'),
      icon: 'pi pi-inbox',
      command: () => this.redirectToNew('request'),
    },
  ];

  isLoading = signal(0);
  payments = signal<ApPayment[]>([]);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(25);
  /** Server-side sort. The table holds one page, so ordering has to happen in the query — sorting here
   *  would reorder 25 rows and present it as the order of all of them.
   *  Defaults match the search's own default: newest payment date first. */
  readonly sortField = signal<string>('paymentDate');
  readonly sortOrder = signal<number>(-1);

  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'ap-payments-filters', config: this.filterConfig });
  bankAccountOptions = signal<SelectOption[]>([]);

  private readonly bankLabels = new Map<string, string>();
  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};
  /** Criteria of the current result set — replayed on paging so a page change keeps the filters. */
  private lastSearchCriteria: ApPaymentSearchCommand = {};

  /** This screen's own type-ahead state — see ProviderFilterService.createConfig. */
  private readonly providerAutocompleteConfig = this.providerFilter.createConfig(inject(DestroyRef));

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as ApPaymentSearchCommand));
  }

  ngOnInit(): void {
    this.statusLabels = this.enumService.getEnum('ApPaymentStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;

    this.filterConfig.set(this.buildFilterConfig());
    this.loadBankAccounts();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: this.translate('ApPayment_Number', 'Payment #'),
          placeholder: this.translate('ApPayments_Search', 'Enter payment number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'invoiceNumber',
          label: this.translate('ApPayment_InvoiceNumber', 'Invoice #'),
          placeholder: this.translate('ApPayments_Filter_Invoice', 'AP or provider invoice number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'providerReference',
          label: this.translate('ApPayment_Provider', 'Provider'),
          placeholder: this.translate('Provider_Search', 'Search provider'),
          autocompleteConfig: this.providerAutocompleteConfig,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.translate('Status', 'Status'),
          placeholder: this.translate('Select_status', 'Select status'),
          options: this.statusLabels,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'bankAccountReference',
          label: this.translate('ApPayment_BankAccount', 'Bank account'),
          placeholder: this.translate('ApPayments_Filter_Bank', 'Select bank account'),
          options: this.bankAccountOptions(),
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'paymentDateFrom',
          label: this.translate('ApPayment_PaymentDateFrom', 'Payment date from'),
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'paymentDateTo',
          label: this.translate('ApPayment_PaymentDateTo', 'Payment date to'),
          defaultVisible: false,
        },
      ],
    };
  }

  private loadBankAccounts(): void {
    this.bankAccountService.getAll().subscribe({
      next: (response) => {
        const accounts = response.data ?? [];
        const options: SelectOption[] = [];
        for (const acc of accounts) {
          if (!acc.id) continue;
          const label = this.buildBankLabel(acc);
          this.bankLabels.set(acc.id, label);
          options.push({ label, value: acc.id });
        }
        this.bankAccountOptions.set(options);
        // Accounts arrive after the panel is already on screen — republish so its options are populated.
        this.filterConfig.set(this.buildFilterConfig());
      },
      error: () => this.bankAccountOptions.set([]),
    });
  }

  /** Bank, currency, last four — currency from the static map rather than the EnumService cache, which
   *  is empty on a cold load and never re-read, silently dropping the code from the label. */
  private buildBankLabel(acc: BankAccount): string {
    const cur = currencyLabelFromApi(acc.currency);
    const last4 = (acc.bankAccountNumber ?? '').slice(-4);
    return [acc.bankName, cur, last4 ? `****${last4}` : ''].filter((s) => s).join(' · ');
  }

  bankLabel(id: string | null | undefined): string {
    if (!id) return '';
    return this.bankLabels.get(id) ?? '';
  }

  /**
   * The invoices a payment settles, for the list column.
   *
   * <p>Read straight off the allocations the search already returned — they carry both numbers, snapshotted
   * at save, so naming the invoices costs no extra call and works on a posted payment whose documents have
   * long since left the open payables.</p>
   *
   * <p>Our AP number first, the provider's printed one only where a document has none: they identify the
   * same invoice, and showing both would double the width of every row to say one thing twice.</p>
   */
  invoiceNumbers(payment: ApPayment): string[] {
    const numbers = (payment.allocations ?? []).map((a) => a.documentNumber || a.fiscalNumber || '').filter((n) => !!n);
    // A payment can settle the same document on more than one line (different cash plans, or a grouped
    // base document and its notes), and the column should name an invoice once.
    return [...new Set(numbers)];
  }

  private search(criteria: ApPaymentSearchCommand): void {
    this.lastSearchCriteria = criteria;
    this.startLoading();
    this.service
      .search(criteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.payments.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: () => {
          this.payments.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: ApPaymentSearchCommand): void {
    // A persisted/URL filter arrives as a string — the API types status as an enum and the dates as ISO, so
    // coerce both back before they leave the page.
    const criteria: ApPaymentSearchCommand = {
      ...filters,
      number: filters.number || null,
      invoiceNumber: filters.invoiceNumber || null,
      // The type-ahead puts the whole suggestion in the control, so the spread above would
      // send an object where the API expects the id.
      providerReference: this.providerFilter.toProviderId(filters.providerReference),
      status: filters.status != null ? (Number(filters.status) as ApPaymentStatus) : null,
      paymentDateFrom: this.toIso(filters.paymentDateFrom),
      paymentDateTo: this.toIso(filters.paymentDateTo),
    };
    this.currentPage.set(1);
    this.search(criteria);
  }

  private toIso(value: unknown): string | null {
    if (!value) return null;
    return value instanceof Date ? value.toISOString() : String(value);
  }

  /**
   * Re-queries on a column sort. Back to page one, because a sorted page 3 is three pages into an order
   * that no longer exists. Guarded, so a sort that changed nothing does not re-fetch.
   */
  onSort(event: SortEvent): void {
    const field = event.field ?? 'paymentDate';
    const order = event.order ?? -1;
    if (field === this.sortField() && order === this.sortOrder()) return;

    this.sortField.set(field);
    this.sortOrder.set(order);
    this.currentPage.set(1);
    this.search(this.lastSearchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.lastSearchCriteria);
  }

  redirectToDetails(id?: string): void {
    this.router.navigate(['/ap-payment-details'], { queryParams: { id: id ?? 'new' } });
  }

  /** Start a new payment, telling the details page which path (direct AP document vs. approved request) to open in. */
  redirectToNew(source: 'document' | 'request'): void {
    this.router.navigate(['/ap-payment-details'], { queryParams: { id: 'new', source } });
  }

  statusLabel(status: number): string {
    return this.statusLabels[status] ?? '';
  }

  statusSeverity(status: number): 'success' | 'info' | 'secondary' {
    if (status === ApPaymentStatus.Posted) return 'info';
    return 'secondary';
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  getRowData(rowData: unknown): ApPayment {
    return rowData as ApPayment;
  }
}
